import { useState } from 'react';
import type { RefObject } from 'react';

import type { ConnectionView } from '@/lib/api/generated/model';

import { useConnectionCatalog } from '../hooks/useConnectionCatalog';
import type { ConnectionProviderType } from '../types';
import { ConnectSheet } from './ConnectSheet';

/**
 * The connection form as a step inside another form (spec 124 US1).
 *
 * Opened over the trigger form or the execution-image form when the
 * connection they need does not exist: the provider is the one the outer form
 * needs and cannot be changed. The sheet is the Connections page's own
 * `ConnectSheet` — paste a token (or, for a registry, name it and give its
 * address) and the connection is made — so a connection looks and behaves the
 * same wherever it is created. A sheet over a sheet rather than a dialog.
 *
 * The outer form learns one thing — the connection that now exists — through
 * `onCreated`, and the invalidation the create performs refills the outer
 * picker. Focus returns to that picker through `finalFocus`, however this
 * closes.
 */
export function CreateConnectionSheet({
  workspaceId,
  open,
  providerType,
  finalFocus,
  onCreated,
  onClose,
}: {
  workspaceId: string;
  open: boolean;
  providerType: ConnectionProviderType;
  finalFocus?: RefObject<HTMLElement | null>;
  /** Called with the new connection before `onClose`. */
  onCreated: (connection: ConnectionView) => void;
  onClose: () => void;
}) {
  const catalog = useConnectionCatalog(workspaceId);
  const provider = [
    ...(catalog.providers ?? []),
    ...(catalog.multiProviders ?? []),
  ].find((p) => p.type === providerType);

  // Kept mounted, and closed through `open`, so Base UI runs its close — the
  // exit, and `finalFocus` back to the outer picker. Unmounting it instead
  // skipped both. A fresh form each time it opens (a new `key` per opening).
  const [session, setSession] = useState(open ? 1 : 0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSession((n) => n + 1);
  }

  // Nothing until first asked for, and nothing until the catalog is in.
  if (session === 0 || catalog.isPending || !provider) return null;

  return (
    <ConnectSheet
      key={`${providerType}:${String(session)}`}
      workspaceId={workspaceId}
      provider={provider}
      open={open}
      finalFocus={finalFocus}
      onSaved={onCreated}
      onClose={onClose}
    />
  );
}
